// Engagement Sprint W3.3 — fire GA4 milestone events when a user crosses
// 50%, 75%, or 100% profile completeness. Crossings are tracked per-account
// in localStorage so a user who drops below a threshold and crosses again
// doesn't re-fire (once-per-account-per-threshold semantics).
//
// The score itself is server-computed by GET /api/profile/completeness.

import { trackEvent } from '@/lib/tracking';

const STORAGE_KEY = 'js_completeness_crossed';
const THRESHOLDS = [50, 75, 100] as const;

function readCrossed(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === 'number');
  } catch {
    return [];
  }
}

function writeCrossed(values: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {}
}

/**
 * Call this every time the dashboard receives a fresh completeness score.
 * Fires `profile_completeness_milestone` with `{ threshold }` for each
 * newly-crossed gate in [50, 75, 100].
 */
export function recordCompletenessScore(score: number): void {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(score) || score < 0) return;

  const crossed = readCrossed();
  let dirty = false;

  for (const t of THRESHOLDS) {
    if (score >= t && !crossed.includes(t)) {
      crossed.push(t);
      dirty = true;
      try { trackEvent('profile_completeness_milestone', { threshold: t }); } catch {}
    }
  }

  if (dirty) writeCrossed(crossed);
}
